import React from 'react';
import { BarSegment, useChart } from '@chakra-ui/charts';
import { Box } from '@chakra-ui/react';

interface ProgressItem {
  percent: number;
  description: string;
}

interface MapProgressBarProps {
  phProportions: ProgressItem[];
}

// pH color mapping by range:
// >7.5 = brown, 6.8-7.5 = orange, 6.1-6.7 = green, 5.5-6.0 = yellow, <5.5 = red
const getPhColor = (description: string): string => {
  const normalized = (description || '').toLowerCase().replace(/\s+/g, '');

  // Explicit comparator patterns
  if (normalized.includes('>7.5') || normalized.includes('≥7.6')) {
    return '#8B4513'; // brown
  }
  if (normalized.includes('<5.5') || normalized.includes('≤5.5')) {
    return '#F56565'; // red
  }

  // Range patterns like 6.8-7.5, 6.1-6.7, 5.5-6.0 (support different dash chars)
  const rangeMatch = normalized.match(/(\d+(?:\.\d+)?)[-–—](\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const min = parseFloat(rangeMatch[1]);
    const max = parseFloat(rangeMatch[2]);
    if (!isNaN(min) && !isNaN(max)) {
      if (min >= 6.8 && max <= 7.5) return '#ED8936'; // orange.500
      if (min >= 6.1 && max <= 6.7) return '#48BB78'; // green.400
      if (min >= 5.5 && max <= 6.0) return '#ECC94B'; // yellow.400
    }
  }

  // Fallback: try to infer with any numbers present
  const nums = (normalized.match(/\d+(?:\.\d+)?/g) || []).map(parseFloat).filter(n => !isNaN(n));
  if (nums.length === 1) {
    const n = nums[0];
    if (n > 7.5) return '#8B4513'; // brown
    if (n >= 6.8 && n <= 7.5) return '#ED8936'; // orange
    if (n >= 6.1 && n <= 6.7) return '#48BB78'; // green
    if (n >= 5.5 && n <= 6.0) return '#ECC94B'; // yellow
    if (n < 5.5) return '#F56565'; // red
  }

  return '#A0AEC0'; // gray.400 fallback
};

const MapProgressBar: React.FC<MapProgressBarProps> = ({ phProportions }) => {
  const chart = useChart({
    sort: { by: 'value', direction: 'desc' },
    data: phProportions.map((item) => ({
      name: item.description,
      value: item.percent,
      color: getPhColor(item.description),
    })),
  });

  return (
    <Box 
      className='progress' 
      minHeight="50px"
      minWidth="200px"
      width="100%"
      height="100%"
    >
      <BarSegment.Root chart={chart} barSize='5'>
        <BarSegment.Content>
          <BarSegment.Bar />
        </BarSegment.Content>
        <BarSegment.Legend gap='2' textStyle='xs' showPercent />
      </BarSegment.Root>
    </Box>
  );
};

export default MapProgressBar;
